import { ImperansSubmissionOutcome } from "../../Enums/ImperansSubmissionOutcome.enum";
import { IImperansSubmission } from "../../Interfaces/IImperansSubmission";
import { IOnboardingImportIssue } from "../../Interfaces/IOnboardingOutputs";
import { ONBOARDING_STEP_LABELS } from "./OnboardingWizard.utils";

/**
 * How the review page reads an Imperans answer (Epic 6303, Feature CRM1, stories 6339 and 6340).
 * Pure functions, so the panel's wording and grouping are tested without rendering.
 */

export const STILL_CHECKING_MESSAGE = "Still checking with Imperans…";
export const CONFIGURATION_STEP_LABEL = "Configuration — no step can fix this";
export const KEPT_IN_CRM_TITLE = "Kept in the CRM, not in Imperans";
export const SELF_CHECK_FAILED_TITLE = "Not sent — the CRM's check found items to fix";

/** A submit the CRM stopped before anything was frozen or sent: the record is unchanged (story 6340). */
export const changedNothing = (submission: IImperansSubmission | null): boolean =>
  submission?.outcome === ImperansSubmissionOutcome.SelfCheckFailed;

export type SubmissionSeverity = "success" | "warning" | "error" | "info";

export interface IIssueStepGroup {
  /** 1 to 6, or null for source.* and unrecognised paths. */
  step: number | null;
  title: string;
  issues: IOnboardingImportIssue[];
}

/** Errors grouped by the wizard step that owns them, steps in order, configuration last (story 6340). */
export const groupIssuesByStep = (issues: IOnboardingImportIssue[]): IIssueStepGroup[] => {
  const groups = new Map<number | null, IOnboardingImportIssue[]>();
  issues.forEach((issue) => {
    const key = issue.step ?? null;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(issue);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a ?? Number.MAX_SAFE_INTEGER) - (b ?? Number.MAX_SAFE_INTEGER))
    .map(([step, grouped]) => ({
      step,
      title: step === null ? CONFIGURATION_STEP_LABEL : `Step ${step} — ${ONBOARDING_STEP_LABELS[step - 1]}`,
      issues: grouped,
    }));
};

/** "C12 — message": Imperans's check id stays beside its message, verbatim (story 6339). */
export const issueLine = (issue: IOnboardingImportIssue): string =>
  issue.check ? `${issue.check} — ${issue.message}` : issue.message;

export const submissionSeverity = (submission: IImperansSubmission | null): SubmissionSeverity => {
  if (!submission) return "info";
  if (submission.isCreated) return "success";
  switch (submission.outcome) {
    case ImperansSubmissionOutcome.Rejected:
    case ImperansSubmissionOutcome.SelfCheckFailed:
      return "error";
    case ImperansSubmissionOutcome.Pending:
    case ImperansSubmissionOutcome.KeptInCrmOnly:
      return "info";
    default:
      return "warning";
  }
};

export const submissionTitle = (submission: IImperansSubmission | null): string => {
  if (!submission) return "Not yet sent to Imperans";
  switch (submission.outcome) {
    case ImperansSubmissionOutcome.Created:
    case ImperansSubmissionOutcome.Replayed:
      return "Created in Imperans as Draft";
    case ImperansSubmissionOutcome.AlreadyCreated:
      return "Already created in Imperans";
    case ImperansSubmissionOutcome.Rejected:
      return "Imperans rejected the onboarding";
    case ImperansSubmissionOutcome.Refused:
      return "Imperans refused the request";
    case ImperansSubmissionOutcome.Failed:
      return "Imperans could not create the client";
    case ImperansSubmissionOutcome.TimedOut:
      return "Imperans did not answer in time";
    case ImperansSubmissionOutcome.NotPaired:
      return "Not paired with an Imperans API";
    case ImperansSubmissionOutcome.KeptInCrmOnly:
      return "Recorded in the CRM only";
    case ImperansSubmissionOutcome.Pending:
      return STILL_CHECKING_MESSAGE;
    case ImperansSubmissionOutcome.SelfCheckFailed:
      return SELF_CHECK_FAILED_TITLE;
    default:
      return "Imperans answer";
  }
};

/** Shown in place of Mark as Current Client while Imperans holds the client as Draft (Feature CRM4). */
export const AWAITS_IMPERANS_ACTIVATION_MESSAGE = "Becomes a Current Client when Imperans activates it.";

/**
 * Imperans created the client as Draft, so the CRM no longer promotes it itself: activation in
 * Imperans moves the record to Current Client through the callback (Feature CRM4). Step 7 and the
 * Overview card both read this, so Mark as Current Client is offered on neither. A client recorded
 * in the CRM only (Imperans Link) was never created there and keeps the action.
 */
export const awaitsImperansActivation = (submission: IImperansSubmission | null): boolean =>
  Boolean(submission?.isCreated);

/** The regional client gets no review ticket, so its kept-in-CRM content is shown here (story 6341). */
export const showsKeptInCrm = (submission: IImperansSubmission | null): boolean =>
  Boolean(submission?.isCreated && !submission.isNational && submission.keptInCrm.length > 0);
